/* BoosterLoginModal — add or re-login a REST-only booster session.
 *
 * A booster is a secondary OnlyFans login (slot 2-3) that exists purely to
 * multiply the account's REST budget — it holds no realtime socket and never
 * touches the primary (slot 1). Unlike the primary, a booster may egress its OWN
 * IP for extra rate-limit headroom, so step 1 lets the operator choose the proxy:
 *   • auto-provision a distinct one from the pool (the default — a booster only
 *     helps on its OWN IP, since the rate limit is per egress IP; ADR 0008),
 *   • a manually entered proxy, or
 *   • the account's current proxy (shares the primary's IP — warned, since it
 *     gives no rate-limit relief).
 * The choice never overwrites the account's stored proxy; it is used for this
 * login and persisted per-slot. Step 2 is the live LoginStream (role="booster").
 * `slot` pins a specific slot when re-logging a dead one; omitted, the brain
 * assigns the lowest free slot.
 */
import { useState } from 'react';
import { LoginStream } from './LoginStream';
import { C } from './onboardingKit';

interface BoosterLoginModalProps {
  accountId: string;
  /** Pin a slot (2-3) when re-logging a dead booster; omit to add a new one. */
  slot?: number;
  onClose: () => void;
  onConnected?: () => void;
}

type ProxyMode = 'same' | 'auto' | 'manual';

const OPTIONS: { mode: ProxyMode; title: string; hint: string; warn?: string }[] = [
  { mode: 'auto', title: 'Auto-provision new', hint: 'Claim a distinct proxy from the pool for its own IP (recommended).' },
  { mode: 'manual', title: 'Manual proxy', hint: 'Enter a specific proxy to log in through.' },
  {
    mode: 'same',
    title: "Account's current proxy",
    hint: 'Share the primary session’s IP.',
    warn: 'Same IP as the primary — a booster here shares one rate-limit budget and gives no relief.',
  },
];

export function BoosterLoginModal({ accountId, slot, onClose, onConnected }: BoosterLoginModalProps) {
  const title = slot ? `Re-login Login Session ${slot}` : 'Add login session';
  const [mode, setMode] = useState<ProxyMode>('auto');
  const [manualUrl, setManualUrl] = useState('');
  const [started, setStarted] = useState(false);

  const manualReady = mode !== 'manual' || manualUrl.trim().length > 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 760, maxWidth: '100%', maxHeight: 'calc(92 * var(--om-vh))', overflowY: 'auto',
          background: 'var(--om-card)', border: `1px solid ${C.line2}`, borderRadius: 18, padding: 26,
          boxShadow: '0 24px 70px rgba(0,0,0,.6)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ color: C.text, fontSize: 20, fontWeight: 800, margin: 0 }}>{title}</h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {!started ? (
          <>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.5, margin: '0 0 18px' }}>
              Sign in to an extra OnlyFans login for this account. It won&rsquo;t affect the
              primary session. First, choose the proxy this login should use.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              {OPTIONS.map((o) => {
                const active = mode === o.mode;
                return (
                  <button
                    key={o.mode}
                    onClick={() => setMode(o.mode)}
                    style={{
                      textAlign: 'left', cursor: 'pointer', borderRadius: 12, padding: '12px 14px',
                      border: `1px solid ${active ? C.purpleHi : C.line2}`,
                      background: active ? 'color-mix(in srgb, var(--om-accent) 12%, transparent)' : 'transparent',
                    }}
                  >
                    <div style={{ color: C.text, fontWeight: 700, fontSize: 14.5 }}>{o.title}</div>
                    <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{o.hint}</div>
                    {o.warn && (
                      <div style={{ color: '#f2b34a', fontSize: 12.5, marginTop: 6, display: 'flex', gap: 6, lineHeight: 1.4 }}>
                        <span aria-hidden>⚠</span>
                        <span>{o.warn}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {mode === 'manual' && (
              <input
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                placeholder="http://user:pass@host:port"
                spellCheck={false}
                autoComplete="off"
                style={{
                  width: '100%', boxSizing: 'border-box', marginBottom: 18, padding: '11px 13px',
                  borderRadius: 10, border: `1px solid ${C.line2}`, background: 'var(--om-legacy-page)',
                  color: C.text, fontSize: 14, fontFamily: 'inherit',
                }}
              />
            )}
            <button
              className="om-btn"
              disabled={!manualReady}
              onClick={() => setStarted(true)}
              style={{
                fontFamily: 'inherit', fontWeight: 700, fontSize: 15, borderRadius: 12, padding: '12px 22px',
                border: 'none', cursor: manualReady ? 'pointer' : 'default',
                background: manualReady ? C.purpleHi : 'color-mix(in srgb, var(--om-accent) 25%, transparent)',
                color: '#fff', opacity: manualReady ? 1 : 0.6,
              }}
            >
              Continue
            </button>
          </>
        ) : (
          <>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.5, margin: '0 0 18px' }}>
              Sign in to your OnlyFans login. Press Done once you&rsquo;re on the account.
            </p>
            <LoginStream
              accountId={accountId}
              role="booster"
              slot={slot}
              boosterProxy={{ mode, proxyUrl: mode === 'manual' ? manualUrl.trim() : undefined }}
              onConnected={() => {
                onConnected?.();
                onClose();
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
